import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Download, Target, RefreshCw, AlertTriangle, Copy, Link2, CheckCircle, ChevronDown, ChevronUp, Image as ImageIcon, Mail, Phone, Send, Loader2, Lock, DollarSign, ArrowDownWideNarrow, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FocalPointSelector } from '../../FocalPointSelector';
import DuplicatePhotosDialog from '../../DuplicatePhotosDialog';

const SettingsPanel = ({
  gallery,
  galleryId,
  photos,
  analytics,
  processingStatus,
  onRefreshGallery,
  axiosConfig,
  backendUrl
}) => {
  const [finalizing, setFinalizing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showDownloadUrlsDialog, setShowDownloadUrlsDialog] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState({
    full_size_url: '',
    social_size_url: ''
  });
  const [savingUrls, setSavingUrls] = useState(false);
  const [showFocalPointDialog, setShowFocalPointDialog] = useState(false);
  const [showDuplicatePhotosDialog, setShowDuplicatePhotosDialog] = useState(false);
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [paywallEnabled, setPaywallEnabled] = useState(gallery?.paywall_enabled || false);
  const [paywallUrl, setPaywallUrl] = useState(gallery?.paywall_url || '');
  const [contractUrl, setContractUrl] = useState(gallery?.contract_url || '');
  const [savingPaywall, setSavingPaywall] = useState(false);
  const [defaultSort, setDefaultSort] = useState(gallery?.default_sort || 'newest');
  const [savingSort, setSavingSort] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  // Sync paywall state when gallery prop updates (e.g. after refresh)
  useEffect(() => {
    setPaywallEnabled(gallery?.paywall_enabled || false);
    setPaywallUrl(gallery?.paywall_url || '');
    setContractUrl(gallery?.contract_url || '');
  }, [gallery?.paywall_enabled, gallery?.paywall_url, gallery?.contract_url]);

  useEffect(() => {
    setDefaultSort(gallery?.default_sort || 'newest');
  }, [gallery?.default_sort]);

  const getEstimatedTime = (photoCount) => {
    if (photoCount < 50) return "1-2 minutes";
    if (photoCount < 200) return "2-5 minutes";
    if (photoCount < 500) return "5-10 minutes";
    return "10-15 minutes";
  };

  const handleFinalizeGallery = async () => {
    if (!window.confirm('Finalize this gallery? This will generate download zips (Full Size and Social Size) that will be available for 6 months.')) {
      return;
    }

    try {
      setFinalizing(true);
      toast.info('Starting gallery finalization...');

      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/finalize`,
        {},
        axiosConfig
      );

      toast.success('Gallery finalization started! This may take a few minutes.');
      onRefreshGallery();
    } catch (error) {
      console.error('Error finalizing gallery:', error);
      toast.error(`Failed to finalize gallery: ${error.response?.data?.detail || error.message}`);
    } finally {
      setFinalizing(false);
    }
  };

  const handleRegenerateDownloads = async () => {
    if (!window.confirm('Regenerate download zips? This will delete old zips and create new ones with all current photos.')) {
      return;
    }

    try {
      setRegenerating(true);
      toast.info('Regenerating download zips...');

      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/regenerate-downloads`,
        {},
        axiosConfig
      );

      toast.success('Download zips regeneration started!');
      onRefreshGallery();
    } catch (error) {
      console.error('Error regenerating downloads:', error);
      toast.error(`Failed to regenerate: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveDownloadUrls = async () => {
    if (!downloadUrls.full_size_url && !downloadUrls.social_size_url) {
      toast.error('Please enter at least one download URL');
      return;
    }

    try {
      setSavingUrls(true);

      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/download-urls`,
        downloadUrls,
        axiosConfig
      );

      toast.success('Download URLs saved successfully!');
      setShowDownloadUrlsDialog(false);
      setDownloadUrls({ full_size_url: '', social_size_url: '' });
      onRefreshGallery();
    } catch (error) {
      console.error('Error saving download URLs:', error);
      toast.error(`Failed to save URLs: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingUrls(false);
    }
  };

  const handleResetFinalization = async () => {
    if (!window.confirm('Reset finalization? This will allow you to finalize the gallery again. Current download links will be removed.')) {
      return;
    }

    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/reset-finalization`,
        {},
        axiosConfig
      );

      toast.success('Finalization reset! You can now finalize again.');
      onRefreshGallery();
    } catch (error) {
      console.error('Error resetting finalization:', error);
      toast.error(`Failed to reset: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleRegenerateLinks = async () => {
    if (!window.confirm('Regenerate download links? This will create fresh links valid for 6 months.')) {
      return;
    }

    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/regenerate-links`,
        {},
        axiosConfig
      );

      toast.success('Download links regenerated! Valid for 6 months.');
      onRefreshGallery();
    } catch (error) {
      console.error('Error regenerating links:', error);
      toast.error(`Failed to regenerate: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleGenerateAITop30 = async () => {
    try {
      toast.info('Generating AI Top 30 selection...');

      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/ai-top30/generate`,
        {},
        axiosConfig
      );

      toast.success('AI Top 30 generated successfully!');
      onRefreshGallery();
    } catch (error) {
      console.error('Error generating AI Top 30:', error);
      toast.error(`Failed to generate AI Top 30: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDisableAITop30 = async () => {
    try {
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/ai-top30`,
        axiosConfig
      );

      toast.success('AI Top 30 disabled');
      onRefreshGallery();
    } catch (error) {
      console.error('Error disabling AI Top 30:', error);
      toast.error(`Failed to disable AI Top 30: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSaveFocalPoint = async (focalPoint) => {
    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/featured-focal-point`,
        focalPoint,
        axiosConfig
      );

      toast.success('Focal point saved!');
      setShowFocalPointDialog(false);
      onRefreshGallery();
    } catch (error) {
      console.error('Error saving focal point:', error);
      toast.error('Failed to save focal point');
    }
  };

  const handleUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      e.target.value = '';
      return;
    }
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/cover-image`,
        formData,
        { ...axiosConfig, headers: { ...(axiosConfig?.headers || {}), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Cover image updated');
      onRefreshGallery();
    } catch (error) {
      console.error('Error uploading cover image:', error);
      toast.error(error?.response?.data?.detail || 'Failed to upload cover image');
    } finally {
      setCoverUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveCover = async () => {
    setCoverUploading(true);
    try {
      await axios.delete(`${backendUrl}/api/galleries/${galleryId}/cover-image`, axiosConfig);
      toast.success('Custom cover removed');
      onRefreshGallery();
    } catch (error) {
      console.error('Error removing cover image:', error);
      toast.error('Failed to remove cover image');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(
        `${backendUrl}/api/admin/test-email`,
        {},
        axiosConfig
      );

      if (response.data.success) {
        toast.success('Test email sent to hello@turbo360.com.au');
      } else {
        toast.error(response.data.message || 'Email test failed');
      }
    } catch (error) {
      console.error('Error testing email:', error);
      toast.error(error.response?.data?.detail || 'Email test failed');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const response = await axios.post(
        `${backendUrl}/api/admin/test-sms`,
        {},
        axiosConfig
      );

      if (response.data.success) {
        toast.success('Test SMS sent to +61499944333');
      } else {
        toast.error(response.data.message || 'SMS test failed');
      }
    } catch (error) {
      console.error('Error testing SMS:', error);
      toast.error(error.response?.data?.detail || 'SMS test failed');
    } finally {
      setTestingSms(false);
    }
  };

  const handleSavePaywall = async () => {
    try {
      setSavingPaywall(true);
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/paywall`,
        { enabled: paywallEnabled, payment_url: paywallUrl, contract_url: contractUrl },
        axiosConfig
      );
      toast.success(paywallEnabled ? 'Paywall enabled' : 'Paywall disabled');
      onRefreshGallery();
    } catch (error) {
      console.error('Error saving paywall:', error);
      toast.error('Failed to save paywall settings');
    } finally {
      setSavingPaywall(false);
    }
  };

  const handleSaveDefaultSort = async (value) => {
    try {
      setSavingSort(true);
      setDefaultSort(value);
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/default-sort`,
        { default_sort: value },
        axiosConfig
      );
      toast.success(`Default order: ${value === 'newest' ? 'Newest first' : 'Oldest first'}`);
      onRefreshGallery();
    } catch (error) {
      console.error('Error saving default sort:', error);
      toast.error('Failed to save default order');
    } finally {
      setSavingSort(false);
    }
  };

  // Check if featured photo exists - use gallery's stored URL first, fallback to searching photos
  const featuredPhotoFromPhotos = gallery?.featured_photo_id
    ? photos.find(p => p.id === gallery.featured_photo_id)
    : null;

  // Use gallery's stored featured photo info (always available) or search in loaded photos
  const hasFeaturedPhoto = !!gallery?.featured_photo_id;
  const featuredImageUrl = gallery?.featured_thumbnail_url
    || gallery?.featured_photo_thumbnail
    || featuredPhotoFromPhotos?.preview_url
    || (featuredPhotoFromPhotos?.preview_path ? `${backendUrl}/api/photos/file/previews/${featuredPhotoFromPhotos.preview_path.split('/').pop()}` : null);
  const featuredFilename = featuredPhotoFromPhotos?.filename || gallery?.featured_photo_filename || 'Featured Photo';

  return (
    <div className="h-full overflow-auto p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
          Settings
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Configure gallery downloads and AI features
        </p>
      </div>

      {/* Download Management Section */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-900/20 dark:to-indigo-800/10">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500 rounded-xl shadow-lg shadow-indigo-500/20">
                <Download className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-slate-800 dark:text-white">Download Management</span>
            </div>
            {gallery?.finalized ? (
              <Badge className="bg-green-500 text-white">
                <CheckCircle className="w-3 h-3 mr-1" />
                Finalized
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500 border-slate-300">
                Not Finalized
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 md:gap-6">
            <div className="flex-1">
              {gallery?.finalized ? (
                <div className="space-y-3">
                  {gallery.finalized_at && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Finalized on {new Date(gallery.finalized_at).toLocaleDateString()}
                      {gallery.zip_expires_at && ` • Expires: ${new Date(gallery.zip_expires_at).toLocaleDateString()}`}
                    </p>
                  )}

                  {processingStatus.isProcessing ? (
                    <div className="space-y-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800/30">
                      <p className="text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating download zips...
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {processingStatus.fullSizeComplete ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Full Size Ready
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            Full Size Processing
                          </Badge>
                        )}
                        {processingStatus.socialSizeComplete ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Social Size Ready
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            Social Size Processing
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Full Size Ready
                      </Badge>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Social Size Ready
                      </Badge>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Finalize this gallery to generate pre-built download zips for public users.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      Full Size + Social Size zips
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      Valid for 6 months
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      No delays for users
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <RefreshCw className="w-3 h-3 text-indigo-500" />
                      Est: {getEstimatedTime(analytics.total_photos)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {gallery?.finalized ? (
                <>
                  <Button
                    onClick={handleRegenerateDownloads}
                    disabled={regenerating || processingStatus.isProcessing}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {regenerating || processingStatus.isProcessing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerate Zips
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowDownloadUrlsDialog(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Update URLs
                  </Button>
                  <Button
                    onClick={handleRegenerateLinks}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Links
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleFinalizeGallery}
                    disabled={finalizing || !analytics.total_photos || processingStatus.isProcessing}
                    className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
                  >
                    {finalizing || processingStatus.isProcessing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Finalize Gallery
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowDownloadUrlsDialog(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Add Download URLs
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gallery Display — default photo ordering on share links */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-sky-50 to-blue-100/50 dark:from-sky-900/20 dark:to-blue-800/10">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-xl shadow-lg shadow-blue-500/20">
              <ArrowDownWideNarrow className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-800 dark:text-white">Gallery Display</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-5 space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Default photo order when visitors open the share links. They can still re-sort from the gallery toolbar.
          </p>
          <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Default order</Label>
          <div className="inline-flex w-full sm:w-auto p-1 rounded-xl bg-slate-100 dark:bg-slate-700/50">
            {[
              { id: 'newest', label: 'Newest first' },
              { id: 'oldest', label: 'Oldest first' },
            ].map((opt) => {
              const on = defaultSort === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => !savingSort && opt.id !== defaultSort && handleSaveDefaultSort(opt.id)}
                  disabled={savingSort}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    on ? 'bg-[#BF212F] text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {savingSort && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>
          )}
        </CardContent>
      </Card>

      {/* Payment / Paywall Section */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-rose-50 to-red-100/50 dark:from-rose-900/20 dark:to-red-800/10">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500 rounded-xl shadow-lg shadow-rose-500/20">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-slate-800 dark:text-white">Payment / Paywall</span>
            </div>
            {paywallEnabled && (
              <Badge className="bg-red-500 text-white">
                <Lock className="w-3 h-3 mr-1" />
                Active
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-5 space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Lock downloads behind payment. Users can still browse and view images but cannot download until payment is received.
          </p>

          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Enable Download Paywall</span>
            </div>
            <button
              onClick={() => setPaywallEnabled(!paywallEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${paywallEnabled ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${paywallEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {paywallEnabled && (
            <div className="space-y-2">
              <Label htmlFor="paywall_url">Payment URL</Label>
              <Input
                id="paywall_url"
                placeholder="https://pay.example.com/gallery-payment"
                value={paywallUrl}
                onChange={(e) => setPaywallUrl(e.target.value)}
              />
              <p className="text-xs text-slate-500">Users will be directed to this URL when they click "Pay Now".</p>
            </div>
          )}

          {paywallEnabled && (
            <div className="space-y-2">
              <Label htmlFor="contract_url">Contract URL</Label>
              <Input
                id="contract_url"
                placeholder="https://example.com/contract"
                value={contractUrl}
                onChange={(e) => setContractUrl(e.target.value)}
              />
              <p className="text-xs text-slate-500">If set, a "View Contract" button will appear on the paywall modal.</p>
            </div>
          )}

          <Button
            onClick={handleSavePaywall}
            disabled={savingPaywall}
            className="bg-rose-500 hover:bg-rose-600 text-white"
          >
            {savingPaywall ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Paywall Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* AI Features Section */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-orange-50 to-amber-100/50 dark:from-orange-900/20 dark:to-amber-800/10">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg shadow-orange-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-800 dark:text-white">AI Features</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* AI Top 30 */}
            <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50/50 dark:from-orange-900/10 dark:to-amber-900/10 rounded-xl border border-orange-100 dark:border-orange-800/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Sparkles className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h4 className="font-semibold text-slate-800 dark:text-white">AI Top 30</h4>
                </div>
                {gallery?.ai_top30_enabled && (
                  <Badge className="bg-orange-500 text-white">Active</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                AI-selected best 30 photos based on quality and composition
              </p>
              {photos.length >= 50 ? (
                gallery?.ai_top30_enabled ? (
                  <Button
                    onClick={handleDisableAITop30}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Disable AI Top 30
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerateAITop30}
                    size="sm"
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate AI Top 30
                  </Button>
                )
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400 italic">Requires 50+ photos ({photos.length} current)</p>
              )}
            </div>

            {/* Find Duplicates */}
            <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50/50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-xl border border-purple-100 dark:border-purple-800/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Copy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <h4 className="font-semibold text-slate-800 dark:text-white">Find Duplicates</h4>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Scan for and remove duplicate filenames from the gallery
              </p>
              <Button
                onClick={() => setShowDuplicatePhotosDialog(true)}
                variant="outline"
                size="sm"
                className="w-full border-purple-200 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                disabled={photos.length === 0}
              >
                <Copy className="w-4 h-4 mr-2" />
                Scan for Duplicates
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Testing Section */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-cyan-50 to-teal-100/50 dark:from-cyan-900/20 dark:to-teal-800/10">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl shadow-lg shadow-cyan-500/20">
              <Send className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-800 dark:text-white">System Testing</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-5">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Test email and SMS delivery to verify the notification systems are working correctly.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Test Email */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-xl border border-blue-100 dark:border-blue-800/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="font-semibold text-slate-800 dark:text-white">Test Email</h4>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Send a test headshot delivery email to:
              </p>
              <p className="text-sm font-mono bg-white dark:bg-slate-800 px-3 py-2 rounded-lg mb-3 text-blue-600 dark:text-blue-400">
                hello@turbo360.com.au
              </p>
              <Button
                onClick={handleTestEmail}
                disabled={testingEmail}
                size="sm"
                className="w-full bg-blue-500 hover:bg-blue-600"
              >
                {testingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>

            {/* Test SMS */}
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-xl border border-green-100 dark:border-green-800/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <h4 className="font-semibold text-slate-800 dark:text-white">Test SMS</h4>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Send a test headshot delivery SMS to:
              </p>
              <p className="text-sm font-mono bg-white dark:bg-slate-800 px-3 py-2 rounded-lg mb-3 text-green-600 dark:text-green-400">
                +61 499 944 333
              </p>
              <Button
                onClick={handleTestSms}
                disabled={testingSms}
                size="sm"
                className="w-full bg-green-500 hover:bg-green-600"
              >
                {testingSms ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Send Test SMS
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Featured Photo Section */}
      <Card className="overflow-hidden border-0 shadow-md">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-yellow-100/50 dark:from-amber-900/20 dark:to-yellow-800/10">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl shadow-lg shadow-amber-500/20">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-800 dark:text-white">Featured Photo</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          {hasFeaturedPhoto && featuredImageUrl ? (
            <div className="flex items-start gap-5">
              <div className="w-36 h-36 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 shadow-lg flex-shrink-0">
                <img
                  src={featuredImageUrl}
                  alt="Featured"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                  {featuredFilename}
                </p>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  <Target className="w-3 h-3" />
                  Focal point: {gallery.featured_photo_focal_point?.x || 50}%, {gallery.featured_photo_focal_point?.y || 50}%
                </div>
                <Button
                  onClick={() => setShowFocalPointDialog(true)}
                  variant="outline"
                  size="sm"
                  className="border-amber-200 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Adjust Focal Point
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-600">
              <div className="p-3 bg-slate-100 dark:bg-slate-600 rounded-lg">
                <ImageIcon className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No featured photo set</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Select a photo from the Photos panel to set it as featured</p>
              </div>
            </div>
          )}

          {/* Custom cover image — overrides the featured photo for the cover & social shares */}
          <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Custom cover image</span>
              {gallery?.cover_image_url && (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-0">Active</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Upload a cover used for the gallery card and social share preview. It overrides the featured photo and is <strong>not</strong> added to the photo grid.
            </p>

            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadCover} />

            {gallery?.cover_image_url ? (
              <div className="flex items-start gap-4">
                <div className="w-36 h-36 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 shadow-lg flex-shrink-0">
                  <img src={gallery.cover_image_url} alt="Custom cover" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col gap-2 max-w-xs">
                  <Button
                    onClick={() => coverInputRef.current?.click()}
                    disabled={coverUploading}
                    variant="outline"
                    size="sm"
                    className="justify-start border-amber-200 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  >
                    {coverUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Replace cover
                  </Button>
                  <Button
                    onClick={handleRemoveCover}
                    disabled={coverUploading}
                    variant="outline"
                    size="sm"
                    className="justify-start border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove cover
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
                className="w-full flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-400 dark:hover:border-amber-600 transition-colors text-left disabled:opacity-60"
              >
                <div className="p-3 bg-white dark:bg-slate-600 rounded-lg">
                  {coverUploading ? <Loader2 className="w-6 h-6 text-amber-500 animate-spin" /> : <Upload className="w-6 h-6 text-amber-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{coverUploading ? 'Uploading…' : 'Upload custom cover'}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">JPG or PNG · landscape works best</p>
                </div>
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      {gallery?.finalized && (
        <Card className="overflow-hidden border-0 shadow-md">
          <button
            onClick={() => setDangerZoneOpen(!dangerZoneOpen)}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10 border-b border-red-100 dark:border-red-800/30 hover:from-red-100 hover:to-red-100 dark:hover:from-red-900/30 dark:hover:to-red-800/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-xl shadow-lg shadow-red-500/20">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-red-800 dark:text-red-200">Danger Zone</span>
            </div>
            {dangerZoneOpen ? (
              <ChevronUp className="w-5 h-5 text-red-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-red-500" />
            )}
          </button>

          {dangerZoneOpen && (
            <CardContent className="p-5 bg-red-50/50 dark:bg-red-900/10">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-800/50">
                <div>
                  <p className="font-medium text-red-800 dark:text-red-200">Reset Finalization</p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Remove download links and allow re-finalization
                  </p>
                </div>
                <Button
                  onClick={handleResetFinalization}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-100 hover:border-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Download URLs Dialog */}
      <Dialog open={showDownloadUrlsDialog} onOpenChange={setShowDownloadUrlsDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add Download URLs</DialogTitle>
            <DialogDescription>
              Paste the download URLs from the email you received.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="full_size_url">Full Size Zip URL</Label>
              <Input
                id="full_size_url"
                placeholder="https://..."
                value={downloadUrls.full_size_url}
                onChange={(e) => setDownloadUrls({ ...downloadUrls, full_size_url: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="social_size_url">Social Size Zip URL (2048px)</Label>
              <Input
                id="social_size_url"
                placeholder="https://..."
                value={downloadUrls.social_size_url}
                onChange={(e) => setDownloadUrls({ ...downloadUrls, social_size_url: e.target.value })}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDownloadUrlsDialog(false);
                setDownloadUrls({ full_size_url: '', social_size_url: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDownloadUrls}
              disabled={savingUrls || (!downloadUrls.full_size_url && !downloadUrls.social_size_url)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingUrls ? 'Saving...' : 'Save URLs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Focal Point Dialog */}
      <FocalPointSelector
        open={showFocalPointDialog}
        onClose={() => setShowFocalPointDialog(false)}
        imageUrl={featuredImageUrl}
        currentFocalPoint={gallery?.featured_photo_focal_point || { x: 50, y: 50 }}
        onSave={handleSaveFocalPoint}
      />

      {/* Duplicate Photos Dialog */}
      <DuplicatePhotosDialog
        open={showDuplicatePhotosDialog}
        onOpenChange={setShowDuplicatePhotosDialog}
        galleryId={galleryId}
        axiosConfig={axiosConfig}
        backendUrl={backendUrl}
      />
    </div>
  );
};

export default SettingsPanel;
